/**
 * Level (roll) from the gravity vector — the framing helper for premises stills
 * (SPEC_STAMP_ROUND_20260924 §D, SPEC_CAPTURE_UI_20260925 §3).
 *
 * NOT evidence. The reading is never sent, never bound into a header, never summarised into
 * X-SV-IMU (that stays the per-chunk CLIP summary in imu.ts). It exists so a signboard or a door
 * plate is photographed straight — a quality aid for the officer's read and for the OCR, nothing more.
 *
 * Pure functions here; the DeviceMotion subscription is `useLevel.ts`.
 *
 * Frame conventions (verified against the W3C DeviceMotion example: a phone flat on a table,
 * screen up, reports accelerationIncludingGravity {x: 0, y: 0, z: +9.81}):
 *   • device x → right edge, y → top edge, z → out of the screen;
 *   • a phone held upright in portrait reports y ≈ +9.81; tilting its top edge to the RIGHT
 *     (clockwise as the holder sees it) by θ gives (−9.81·sin θ, 9.81·cos θ);
 *   • so `roll = atan2(x, y)` is NEGATIVE for a clockwise lean and POSITIVE for a counter-clockwise
 *     one — hence "right" for roll < 0 and "left" for roll > 0 in `levelWords`;
 *   • iOS Safari reports accelerationIncludingGravity with the OPPOSITE sign to Android/W3C.
 *     Both signs flip together, which is a 180° rotation of the in-plane vector; wrapping the roll
 *     into (−90°, 90°] makes the two platforms agree for any phone that is roughly upright — the
 *     only pose a level is useful in. A phone held upside down reads the same as one held
 *     upright; that is accepted, not hidden.
 *   • landscape: `screen.orientation.angle` (0/90/180/270) rotates the device frame into the
 *     SCREEN frame first, so the horizon the customer sees is the horizon of the picture.
 */

export interface GravityVector {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

export interface LevelReading {
  /** Smoothed roll in degrees, (−90, 90]; negative = top edge leaning right. */
  readonly roll: number;
  /** True inside the hysteresis band — the line turns solid and the words say "Level". */
  readonly level: boolean;
}

const RAD_TO_DEG = 180 / Math.PI;

/** Below this in-plane magnitude (m/s²) the phone is flat (aimed at the floor or the sky) and roll is meaningless. */
export const MIN_IN_PLANE_G = 2.5;
/** "Level" is entered at ±1.5° … */
export const LEVEL_ENTER_DEG = 1.5;
/** … and left only past ±2.5°, so the line does not flicker at the boundary. */
export const LEVEL_EXIT_DEG = 2.5;
/** EMA weight of the newest sample (§D: α 0.2). */
export const EMA_ALPHA = 0.2;

/** Rotate the device-frame (x, y) into the SCREEN frame for a screen rotated by `angle` degrees. */
function toScreenFrame(x: number, y: number, angle: number): readonly [number, number] {
  switch (((Math.round(angle) % 360) + 360) % 360) {
    case 90:
      return [-y, x];
    case 180:
      return [-x, -y];
    case 270:
      return [y, -x];
    default:
      return [x, y];
  }
}

/**
 * Roll in degrees for a gravity vector, or null when the phone is too flat to have a roll.
 * `screenAngle` is `screen.orientation.angle` (0 for portrait).
 */
export function rollFromGravity(g: GravityVector, screenAngle = 0): number | null {
  if (!Number.isFinite(g.x) || !Number.isFinite(g.y)) return null;
  const [x, y] = toScreenFrame(g.x, g.y, screenAngle);
  if (Math.hypot(x, y) < MIN_IN_PLANE_G) return null;
  let roll = Math.atan2(x, y) * RAD_TO_DEG;
  if (roll > 90) roll -= 180;
  else if (roll <= -90) roll += 180;
  // −0 reads as "0° right" downstream; normalise.
  return roll === 0 ? 0 : roll;
}

/** EMA smoothing + enter/exit hysteresis on the "level" verdict. One instance per viewfinder mount. */
export class LevelSmoother {
  private ema: number | null = null;
  private wasLevel = false;

  constructor(
    private readonly alpha = EMA_ALPHA,
    private readonly enterDeg = LEVEL_ENTER_DEG,
    private readonly exitDeg = LEVEL_EXIT_DEG,
  ) {}

  /** Feed one raw roll (or null for "no reading"); returns the smoothed reading, or null. */
  push(roll: number | null): LevelReading | null {
    if (roll === null) {
      this.reset();
      return null;
    }
    this.ema = this.ema === null ? roll : this.ema + this.alpha * (roll - this.ema);
    const a = Math.abs(this.ema);
    this.wasLevel = this.wasLevel ? a <= this.exitDeg : a <= this.enterDeg;
    return { roll: this.ema, level: this.wasLevel };
  }

  reset(): void {
    this.ema = null;
    this.wasLevel = false;
  }
}

/** The words beside the line: a deck key + the rounded degrees. Colour is never the only signal (WCAG 1.4.1). */
export function levelWords(r: LevelReading): { readonly key: 'sv.capture.level.level' | 'sv.capture.level.left' | 'sv.capture.level.right'; readonly deg: number } {
  if (r.level) return { key: 'sv.capture.level.level', deg: 0 };
  const deg = Math.max(1, Math.round(Math.abs(r.roll)));
  return { key: r.roll > 0 ? 'sv.capture.level.left' : 'sv.capture.level.right', deg };
}

/** `screen.orientation.angle`, with the legacy `window.orientation` fallback (older iOS Safari). 0 when unknown. */
export function screenAngle(): number {
  if (typeof window === 'undefined') return 0;
  const a = window.screen?.orientation?.angle;
  if (typeof a === 'number' && Number.isFinite(a)) return a;
  const legacy = (window as Window & { orientation?: unknown }).orientation;
  return typeof legacy === 'number' && Number.isFinite(legacy) ? legacy : 0;
}
